export type PaidUnifiedPlanKey = 'silver' | 'gold' | 'platinum';
export type UnifiedBillingCycle = 'monthly' | 'semiannual' | 'yearly';
export type PlanDisplayCurrency = 'USD' | 'TZS';
export type UnifiedPaymentMethod = 'mobile_money' | 'stripe';

export const MINI_WEBSITE_TRIAL_DAYS = 14;
export const UNIFIED_YEARLY_DISCOUNT_PERCENT = 17;

export const UNIFIED_PLAN_PRICES_USD: Record<PaidUnifiedPlanKey, number> = {
  silver: 4,
  gold: 8,
  platinum: 15,
};

export const UNIFIED_PLAN_PRICES_TZS: Record<PaidUnifiedPlanKey, number> = {
  silver: 10000,
  gold: 20000,
  platinum: 38000,
};

export const PAID_UNIFIED_PLANS: PaidUnifiedPlanKey[] = ['silver', 'gold', 'platinum'];

export function isPaidUnifiedPlan(plan: string): plan is PaidUnifiedPlanKey {
  return PAID_UNIFIED_PLANS.includes(plan as PaidUnifiedPlanKey);
}

/** Map marketing/API aliases (standard, premium, plan names) to canonical unified plan keys. */
export function resolveUnifiedPlanKey(raw: string | null | undefined): PaidUnifiedPlanKey | null {
  const n = String(raw || '').trim().toLowerCase();
  if (!n) return null;
  if (isPaidUnifiedPlan(n)) return n;
  if (n === 'standard' || n.includes('standard')) return 'silver';
  if (n === 'premium' || n.includes('premium')) return 'gold';
  if (n.includes('platinum')) return 'platinum';
  if (n.includes('gold')) return 'gold';
  if (n.includes('silver')) return 'silver';
  return null;
}

export function usesTzsPlanDisplay(lang: string | null | undefined): boolean {
  const base = String(lang || 'en').split('-')[0].toLowerCase();
  return base === 'sw';
}

export function getDisplayCurrencyForLang(lang: string | null | undefined): PlanDisplayCurrency {
  return usesTzsPlanDisplay(lang) ? 'TZS' : 'USD';
}

export function getCheckoutCurrency(paymentMethod: UnifiedPaymentMethod): PlanDisplayCurrency {
  return paymentMethod === 'stripe' ? 'USD' : 'TZS';
}

export function billingCycleToMonths(cycle: string): number {
  if (cycle === 'yearly') return 12;
  if (cycle === 'semiannual') return 6;
  return 1;
}

function yearlyPrice(monthly: number): number {
  return Math.round(monthly * 12 * (1 - UNIFIED_YEARLY_DISCOUNT_PERCENT / 100));
}

export function getUnifiedPlanPriceUsd(plan: PaidUnifiedPlanKey, cycle: UnifiedBillingCycle): number {
  const monthly = UNIFIED_PLAN_PRICES_USD[plan];
  if (cycle === 'yearly') return yearlyPrice(monthly);
  if (cycle === 'semiannual') return monthly * 6;
  return monthly;
}

export function getUnifiedPlanPriceTzs(plan: PaidUnifiedPlanKey, cycle: UnifiedBillingCycle): number {
  const monthly = UNIFIED_PLAN_PRICES_TZS[plan];
  if (cycle === 'yearly') return yearlyPrice(monthly);
  if (cycle === 'semiannual') return monthly * 6;
  return monthly;
}

export function getUnifiedPlanPrice(
  plan: PaidUnifiedPlanKey,
  cycle: UnifiedBillingCycle,
  currency: PlanDisplayCurrency,
): number {
  return currency === 'TZS'
    ? getUnifiedPlanPriceTzs(plan, cycle)
    : getUnifiedPlanPriceUsd(plan, cycle);
}

/** Supports 1/3/6/12 month selections (e.g. card payment flow). */
export function getUnifiedPlanPriceByMonths(
  plan: PaidUnifiedPlanKey,
  months: number,
  currency: PlanDisplayCurrency,
): number {
  if (months === 12) return getUnifiedPlanPrice(plan, 'yearly', currency);
  if (months === 6) return getUnifiedPlanPrice(plan, 'semiannual', currency);
  const monthly = currency === 'TZS' ? UNIFIED_PLAN_PRICES_TZS[plan] : UNIFIED_PLAN_PRICES_USD[plan];
  return monthly * months;
}

export function formatUsdPrice(amount: number): string {
  return `$${amount % 1 === 0 ? amount : amount.toFixed(2)}`;
}

export function formatTzPrice(amount: number): string {
  return `${Math.round(amount).toLocaleString()} TZS`;
}

export function formatPlanPrice(amount: number, currency: PlanDisplayCurrency): string {
  return currency === 'TZS' ? formatTzPrice(amount) : formatUsdPrice(amount);
}

export function getAlternatePlanPrice(
  plan: PaidUnifiedPlanKey,
  cycle: UnifiedBillingCycle,
  primaryCurrency: PlanDisplayCurrency,
): { amount: number; currency: PlanDisplayCurrency } {
  const alt: PlanDisplayCurrency = primaryCurrency === 'TZS' ? 'USD' : 'TZS';
  return { amount: getUnifiedPlanPrice(plan, cycle, alt), currency: alt };
}

export function getAlternatePlanPriceByMonths(
  plan: PaidUnifiedPlanKey,
  months: number,
  primaryCurrency: PlanDisplayCurrency,
): { amount: number; currency: PlanDisplayCurrency } {
  const alt: PlanDisplayCurrency = primaryCurrency === 'TZS' ? 'USD' : 'TZS';
  return { amount: getUnifiedPlanPriceByMonths(plan, months, alt), currency: alt };
}

export function hasCustomDomainEntitlement(planType?: string | null): boolean {
  return planType === 'gold' || planType === 'platinum';
}

export const UNIFIED_PLAN_META: Record<
  PaidUnifiedPlanKey,
  { accent: string; soft: string; popular?: boolean }
> = {
  silver: { accent: '#3B82F6', soft: '#EFF6FF' },
  gold: { accent: '#EAB308', soft: '#FEFCE8', popular: true },
  platinum: { accent: '#8B5CF6', soft: '#F5F3FF' },
};
